/**
 * Service for Epicor Connect API calls
 */

import { apiClient, getBackofficeAuthHeaders } from './api';
import {
  EpicorConnectDashboard,
  SyncJob,
  SyncJobCreateRequest,
  SyncJobUpdateRequest,
  SyncJobExecuteRequest,
  SyncJobExecution,
  SyncJobExecutionLog,
  SyncJobScheduleUpdate,
  EpicorEnvironmentInfo,
  CronTemplatesResponse,
  SyncJobType
} from '../types/epicorConnect';

class EpicorConnectService {
  private readonly baseUrl = '/backoffice/epicor-connect';

  /**
   * Get Epicor Connect dashboard overview
   */
  async getDashboard(): Promise<EpicorConnectDashboard> {
    try {
      const headers = getBackofficeAuthHeaders();
      const response = await apiClient.get<EpicorConnectDashboard>(
        `${this.baseUrl}/dashboard`,
        headers
      );
      return response;
    } catch (error) {
      console.error('Get dashboard error:', error);
      throw error;
    }
  }

  /**
   * Get list of sync jobs
   */
  async getSyncJobs(
    jobType?: SyncJobType,
    activeOnly: boolean = true
  ): Promise<SyncJob[]> {
    try {
      const headers = getBackofficeAuthHeaders();
      const queryParams = new URLSearchParams();
      
      if (jobType) {
        queryParams.append('job_type', jobType);
      }
      queryParams.append('active_only', activeOnly.toString());

      const response = await apiClient.get<SyncJob[]>(
        `${this.baseUrl}/jobs?${queryParams.toString()}`,
        headers
      );
      return response;
    } catch (error) {
      console.error('Get sync jobs error:', error);
      throw error;
    }
  }

  /**
   * Create a new sync job
   */
  async createSyncJob(jobData: SyncJobCreateRequest): Promise<SyncJob> {
    try {
      const headers = getBackofficeAuthHeaders();
      const response = await apiClient.post<SyncJob>(
        `${this.baseUrl}/jobs`,
        jobData,
        headers
      );
      return response;
    } catch (error) {
      console.error('Create sync job error:', error);
      throw error;
    }
  }

  /**
   * Update an existing sync job
   */
  async updateSyncJob(jobId: string, jobData: SyncJobUpdateRequest): Promise<SyncJob> {
    try {
      const headers = getBackofficeAuthHeaders();
      const response = await apiClient.put<SyncJob>(
        `${this.baseUrl}/jobs/${jobId}`,
        jobData,
        headers
      );
      return response;
    } catch (error) {
      console.error('Update sync job error:', error);
      throw error;
    }
  }

  /**
   * Delete a sync job (soft delete)
   */
  async deleteSyncJob(jobId: string): Promise<{ message: string }> {
    try {
      const headers = getBackofficeAuthHeaders();
      const response = await apiClient.delete<{ message: string }>(
        `${this.baseUrl}/jobs/${jobId}`,
        headers
      );
      return response;
    } catch (error) {
      console.error('Delete sync job error:', error);
      throw error;
    }
  }

  /**
   * Execute a sync job immediately
   */
  async executeSyncJob(jobId: string, request: SyncJobExecuteRequest): Promise<SyncJobExecution> {
    try {
      const headers = getBackofficeAuthHeaders();
      const response = await apiClient.post<SyncJobExecution>(
        `${this.baseUrl}/jobs/${jobId}/execute`,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error('Execute sync job error:', error);
      throw error;
    }
  }

  /**
   * Get execution history for a sync job
   */
  async getSyncJobExecutions(
    jobId: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<SyncJobExecutionLog> {
    try {
      const headers = getBackofficeAuthHeaders();
      const queryParams = new URLSearchParams();
      queryParams.append('page', page.toString());
      queryParams.append('page_size', pageSize.toString());

      const response = await apiClient.get<SyncJobExecutionLog>(
        `${this.baseUrl}/jobs/${jobId}/executions?${queryParams.toString()}`,
        headers
      );
      return response;
    } catch (error) {
      console.error('Get sync job executions error:', error);
      throw error;
    }
  }

  /**
   * Update sync job schedule
   */
  async updateSyncJobSchedule(
    jobId: string,
    scheduleData: SyncJobScheduleUpdate
  ): Promise<SyncJob> {
    try {
      const headers = getBackofficeAuthHeaders();
      const response = await apiClient.put<SyncJob>(
        `${this.baseUrl}/jobs/${jobId}/schedule`,
        scheduleData,
        headers
      );
      return response;
    } catch (error) {
      console.error('Update sync job schedule error:', error);
      throw error;
    }
  }

  /**
   * Get available cron expression templates
   */
  async getCronTemplates(): Promise<CronTemplatesResponse> {
    try {
      const headers = getBackofficeAuthHeaders();
      const response = await apiClient.get<CronTemplatesResponse>(
        `${this.baseUrl}/templates/cron`,
        headers
      );
      return response;
    } catch (error) {
      console.error('Get cron templates error:', error);
      throw error;
    }
  }

  /**
   * Get Epicor environment information
   */
  async getEnvironmentInfo(): Promise<EpicorEnvironmentInfo> {
    try {
      const headers = getBackofficeAuthHeaders();
      const response = await apiClient.get<EpicorEnvironmentInfo>(
        `${this.baseUrl}/environment`,
        headers
      );
      return response;
    } catch (error) {
      console.error('Get environment info error:', error);
      throw error;
    }
  }

  /**
   * Helper method to format execution duration
   */
  formatDuration(seconds?: number): string {
    if (!seconds) return '0s';
    
    if (seconds < 60) {
      return `${seconds}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const remainingMinutes = Math.floor((seconds % 3600) / 60);
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }
  }

  /**
   * Helper method to format success rate
   */
  formatSuccessRate(rate: number): string {
    return `${rate.toFixed(1)}%`;
  }

  /**
   * Helper method to format throughput
   */
  formatThroughput(throughput: number): string {
    if (throughput < 1) {
      return `${(throughput * 60).toFixed(1)}/min`;
    }
    return `${throughput.toFixed(1)}/sec`;
  }

  /**
   * Helper method to get next run time display
   */
  getNextRunDisplay(nextRunAt?: string): string {
    if (!nextRunAt) return 'Manual only';
    
    const nextRun = new Date(nextRunAt);
    const now = new Date();
    const diffMs = nextRun.getTime() - now.getTime();
    
    if (diffMs < 0) return 'Overdue';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours < 1) {
      return `in ${diffMinutes}m`;
    } else if (diffHours < 24) {
      return `in ${diffHours}h ${diffMinutes}m`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      const remainingHours = diffHours % 24;
      return `in ${diffDays}d ${remainingHours}h`;
    }
  }

  /**
   * Helper method to get last run time display
   */
  getLastRunDisplay(lastRunAt?: string): string {
    if (!lastRunAt) return 'Never';
    
    const lastRun = new Date(lastRunAt);
    const now = new Date();
    const diffMs = now.getTime() - lastRun.getTime();
    
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else {
      return `${diffDays}d ago`;
    }
  }

  /**
   * Validate cron expression (basic validation)
   */
  validateCronExpression(expression: string): boolean {
    if (!expression.trim()) return true; // Empty is valid for manual-only
    
    const parts = expression.trim().split(/\s+/);
    if (parts.length !== 5) return false;
    
    // Basic pattern validation (not comprehensive)
    const patterns = [
      /^(\*|[0-5]?\d)$/, // minutes (0-59)
      /^(\*|[01]?\d|2[0-3])$/, // hours (0-23)
      /^(\*|[12]?\d|3[01])$/, // day of month (1-31)
      /^(\*|[0-9]|1[0-2])$/, // month (1-12)
      /^(\*|[0-6])$/ // day of week (0-6, 0=Sunday)
    ];
    
    return parts.every((part, index) => {
      if (part.includes('/')) {
        // Handle step values like */5
        const [base, step] = part.split('/');
        return (base === '*' || patterns[index].test(base)) && /^\d+$/.test(step);
      }
      return patterns[index].test(part);
    });
  }
}

// Create and export singleton instance
export const epicorConnectService = new EpicorConnectService();
export default epicorConnectService;